import { useLayoutEffect, useMemo, useRef } from 'react';

import { buildDefaultValues, buildZodSchemaForFields } from '@klareospace/pharos-lib-front-utils';
import type {
  TemplateContent,
  TemplateFormFieldDef,
  TemplateFormFieldOption,
  TemplateType,
} from '@klareospace/pharos-lib-types';
import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';

import type { InstallShipperDrawerMode } from '../components/install-shipper-drawer';
import { buildEditDefaultValues, buildEditPayload, buildEditSchema } from '../schemas/install-shipper.schema';
import { useDynamicSelectOptions } from './use-dynamic-select-options';
import { useInstallShipper } from './use-install-shipper';
import { useTemplateConfiguration } from './use-template-configuration';
import { useTemplateWithContent } from './use-template-with-content';
import { useUpdateShipper } from './use-update-shipper';

// ─── Effective values resolver ────────────────────────────────────────────────

function resolveEffectiveValues(
  isEditMode: boolean,
  configDetailValues: Record<string, unknown> | undefined,
  existingValues: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (isEditMode && configDetailValues) {
    return configDetailValues;
  }
  return existingValues;
}

// ─── Loading guard ────────────────────────────────────────────────────────────

/**
 * Returns true while any data source required to render the form is still
 * fetching. Dynamic option loading is only relevant after the template fields
 * have been fetched (so we know which sources are actually needed).
 */
function computeIsLoading(
  isLoadingTemplate: boolean,
  isEditMode: boolean,
  isLoadingConfig: boolean,
  isLoadingDynamicOptions: boolean,
): boolean {
  if (isLoadingTemplate) return true;
  if (isEditMode && isLoadingConfig) return true;
  return isLoadingDynamicOptions;
}

// ─── Form-reset helper ────────────────────────────────────────────────────────

interface UseFormResetOnOpenParams {
  effectiveExistingValues: Record<string, unknown> | undefined;
  form: ReturnType<typeof useForm<Record<string, unknown>>>;
  formFields: TemplateFormFieldDef[];
  isEditMode: boolean;
  isLoading: boolean;
  open: boolean;
  templateId: string | null;
  templateWithContent: { content: { formFields: TemplateFormFieldDef[] } } | undefined;
}

/**
 * Fires a form reset exactly once per open+templateId session, but only after
 * ALL required data has loaded. Uses useLayoutEffect for synchronous reset.
 */
function useFormResetOnOpen({
  effectiveExistingValues,
  form,
  formFields,
  isEditMode,
  isLoading,
  open,
  templateId,
  templateWithContent,
}: UseFormResetOnOpenParams): void {
  const prevOpenRef = useRef(false);
  const prevTemplateIdRef = useRef<string | null>(null);
  const hasResetForSessionRef = useRef(false);

  useLayoutEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    const templateChanged = open && templateId !== prevTemplateIdRef.current;
    if (justOpened || templateChanged) {
      hasResetForSessionRef.current = false;
    }
    prevOpenRef.current = open;
    prevTemplateIdRef.current = templateId;
  }, [open, templateId]);

  useLayoutEffect(() => {
    if (!open || hasResetForSessionRef.current) return;
    if (isLoading) return;
    if (templateId !== null && templateWithContent === undefined) return;
    hasResetForSessionRef.current = true;
    form.reset(
      isEditMode
        ? buildEditDefaultValues(formFields, effectiveExistingValues)
        : buildDefaultValues(formFields, effectiveExistingValues),
    );
  }, [effectiveExistingValues, form, formFields, isEditMode, isLoading, open, templateId, templateWithContent]);
}

// ─── Submit helpers ───────────────────────────────────────────────────────────

interface SubmitParams {
  configId: string | null;
  formFields: TemplateFormFieldDef[];
  installMutation: ReturnType<typeof useInstallShipper>;
  isEditMode: boolean;
  templateId: string | null;
  updateMutation: ReturnType<typeof useUpdateShipper>;
}

function handleSubmit(
  values: Record<string, unknown>,
  { configId, formFields, installMutation, isEditMode, templateId, updateMutation }: SubmitParams,
): void {
  if (!templateId) return;
  if (isEditMode && configId) {
    updateMutation.mutate({ id: configId, values: buildEditPayload(formFields, values) });
  } else if (!isEditMode) {
    installMutation.mutate({ templateId, values });
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface UseInstallShipperDrawerParams {
  configId: string | null;
  existingValues?: Record<string, unknown>;
  mode: InstallShipperDrawerMode;
  onClose: () => void;
  open: boolean;
  templateId: string | null;
}

export interface UseInstallShipperDrawerReturn {
  activeMutationPending: boolean;
  /**
   * Resolved options for `dynamic-select` fields, keyed by `optionsSource`.
   * Fetched once when the drawer opens; passed straight through to DynamicFormField.
   */
  dynamicOptions: Record<string, TemplateFormFieldOption[]>;
  form: ReturnType<typeof useForm<Record<string, unknown>>>;
  formFields: TemplateFormFieldDef[];
  /**
   * True while any required data source (template content, config detail in
   * edit mode, or dynamic-select option sources) is still loading.
   *
   * The form MUST NOT render until this is false — this is what guarantees the
   * form reset fires before the first visible paint, eliminating the
   * empty-then-filled flash for both edit-mode values and dynamic-select
   * options.
   */
  isLoading: boolean;
  onSubmit: (values: Record<string, unknown>) => void;
  /** Full template content for section partitioning in the form renderer. */
  templateContent: TemplateContent | undefined;
  /** Template code (e.g. "KLAREO") — used for the drawer identity block. */
  templateCode: string | undefined;
  /** Template logo URL — used for the drawer identity block avatar. */
  templateLogo: string | undefined;
  templateName: string | undefined;
  /** Template type (e.g. "shipper") — used for the drawer identity block category badge. */
  templateType: TemplateType | undefined;
}

export function useInstallShipperDrawer({
  configId,
  existingValues,
  mode,
  onClose,
  open,
  templateId,
}: UseInstallShipperDrawerParams): UseInstallShipperDrawerReturn {
  const isEditMode = mode === 'edit';
  const activeTemplateId = open ? templateId : null;
  const activeConfigId = isEditMode && open ? configId : null;

  const { data: templateWithContent, isLoading: isLoadingTemplate } = useTemplateWithContent(activeTemplateId);

  const { data: configDetail, isLoading: isLoadingConfig } = useTemplateConfiguration(activeConfigId);

  const effectiveExistingValues = useMemo(
    () =>
      resolveEffectiveValues(isEditMode, configDetail?.values as Record<string, unknown> | undefined, existingValues),
    [isEditMode, configDetail, existingValues],
  );

  const formFields = useMemo(() => templateWithContent?.content.formFields ?? [], [templateWithContent]);

  const schema = useMemo(
    () => (isEditMode ? buildEditSchema(formFields) : buildZodSchemaForFields(formFields)),
    [isEditMode, formFields],
  );

  const defaultValues = useMemo(
    () =>
      isEditMode
        ? buildEditDefaultValues(formFields, effectiveExistingValues)
        : buildDefaultValues(formFields, effectiveExistingValues),
    [isEditMode, formFields, effectiveExistingValues],
  );

  const form = useForm<Record<string, unknown>>({
    defaultValues,
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  const { dynamicOptions, isLoading: isLoadingDynamicOptions } = useDynamicSelectOptions(formFields, open);

  const installMutation = useInstallShipper(onClose);
  const updateMutation = useUpdateShipper(onClose);

  // All data sources must be ready before the form is allowed to render.
  // Including dynamic-select option sources prevents the disabled-then-enabled
  // flash on those fields.
  const isLoading = computeIsLoading(isLoadingTemplate, isEditMode, isLoadingConfig, isLoadingDynamicOptions);

  // Reset strategy: fire exactly once per open+templateId session, but only
  // after ALL required data has loaded. See useFormResetOnOpen for details.
  useFormResetOnOpen({
    effectiveExistingValues,
    form,
    formFields,
    isEditMode,
    isLoading,
    open,
    templateId,
    templateWithContent,
  });

  function onSubmit(values: Record<string, unknown>): void {
    handleSubmit(values, { configId, formFields, installMutation, isEditMode, templateId, updateMutation });
  }

  const activeMutationPending = isEditMode ? updateMutation.isPending : installMutation.isPending;

  return {
    activeMutationPending,
    dynamicOptions,
    form,
    formFields,
    isLoading,
    onSubmit,
    templateCode: templateWithContent?.code,
    templateContent: templateWithContent?.content,
    templateLogo: templateWithContent?.logo,
    templateName: templateWithContent?.name,
    templateType: templateWithContent?.type,
  };
}
